import React, { useState } from 'react';
import type { Channel } from '../services/channelService';

interface ChannelCardProps {
  channel: Channel;
  onSelect?: (channel: Channel) => void;
  onStart?: (channel: Channel) => void;
  onStop?: (channel: Channel) => void;
  onDelete?: (channel: Channel) => void;
  selected?: boolean;
}

const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  onSelect,
  onStart,
  onStop,
  onDelete,
  selected,
}) => {
  const [actionLoading, setActionLoading] = useState<'start' | 'stop' | null>(null);
  // Status configuration
  const statusConfig = {
    stopped: {
      color: 'bg-gray-100 text-gray-800',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
        </svg>
      ),
      label: 'Stopped',
      showStart: true,
    },
    starting: {
      color: 'bg-blue-100 text-blue-800',
      icon: (
        <svg className="w-4 h-4 animate-spin" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
        </svg>
      ),
      label: 'Starting...',
      showStart: false,
    },
    running: {
      color: 'bg-green-100 text-green-800',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
        </svg>
      ),
      label: 'Running',
      showStart: false,
    },
    stopping: {
      color: 'bg-yellow-100 text-yellow-800',
      icon: (
        <svg className="w-4 h-4 animate-spin" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
        </svg>
      ),
      label: 'Stopping...',
      showStart: false,
    },
    error: {
      color: 'bg-red-100 text-red-800',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      label: 'Error',
      showStart: true,
    },
  };

  const currentStatus = statusConfig[channel.status];

  // Output format badge
  const formatBadge = {
    hls: { label: 'HLS', color: 'bg-purple-100 text-purple-800' },
    rtmp: { label: 'RTMP', color: 'bg-orange-100 text-orange-800' },
    both: { label: 'HLS + RTMP', color: 'bg-indigo-100 text-indigo-800' },
  };

  const currentFormat = formatBadge[channel.output_format];

  // Format viewers count
  const formatViewers = (count: number): string => {
    if (count === 0) return 'No viewers';
    if (count === 1) return '1 viewer';
    return `${count.toLocaleString()} viewers`;
  };

  // Format watch time (PostgreSQL interval to human readable)
  const formatWatchTime = (interval: string): string => {
    if (interval === '0' || !interval) return '0h';
    // Simple parsing for common formats like "02:30:00" or "1 day 02:30:00"
    const match = interval.match(/(\d+):(\d+):(\d+)/);
    if (match) {
      const hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    }
    return interval;
  };

  const handleStartStop = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (actionLoading) return;
    
    if (channel.status === 'running' || channel.status === 'starting') {
      setActionLoading('stop');
      try {
        await onStop?.(channel);
      } finally {
        setActionLoading(null);
      }
    } else {
      setActionLoading('start');
      try {
        await onStart?.(channel);
      } finally {
        setActionLoading(null);
      }
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete channel "${channel.name}"?`)) {
      onDelete?.(channel);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg ${
        selected ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      {/* Header with status indicator */}
      <div
        className="relative p-6 pb-4 cursor-pointer bg-gradient-to-br from-gray-50 to-white"
        onClick={() => onSelect?.(channel)}
      >
        {/* Status badge */}
        <div className="absolute top-4 right-4">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${currentStatus.color}`}
          >
            {currentStatus.icon}
            {currentStatus.label}
          </span>
        </div>

        {/* Channel name */}
        <h3 className="text-xl font-bold text-gray-900 pr-24 truncate">
          {channel.name}
        </h3>

        {/* Description */}
        {channel.description && (
          <p className="mt-2 text-sm text-gray-600 line-clamp-2">
            {channel.description}
          </p>
        )}

        {/* Status message */}
        {channel.status_message && (
          <p className="mt-2 text-xs text-gray-500 italic">
            {channel.status_message}
          </p>
        )}
      </div>

      {/* Details */}
      <div className="px-6 py-4 space-y-3 border-t border-gray-100">
        {/* Output format and resolution */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-1 rounded text-xs font-medium ${currentFormat.color}`}>
            {currentFormat.label}
          </span>
          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
            {channel.output_resolution.toUpperCase()}
          </span>
          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
            {channel.transcode_preset}
          </span>
        </div>

        {/* Metrics */}
        {channel.status === 'running' && (
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              <span>{formatViewers(channel.current_viewers)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span>{formatWatchTime(channel.total_watch_time)}</span>
            </div>
          </div>
        )}

        {/* Additional info */}
        <div className="text-xs text-gray-500">
          <div className="flex items-center gap-2">
            {channel.low_latency_mode && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                Low Latency
              </span>
            )}
            {channel.dvr_window > 0 && (
              <span>DVR: {Math.floor(channel.dvr_window / 60)}min</span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 flex items-center gap-2 border-t border-gray-200">
        {/* Start/Stop button */}
        <button
          onClick={handleStartStop}
          disabled={channel.status === 'starting' || channel.status === 'stopping' || actionLoading !== null}
          className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            currentStatus.showStart
              ? 'bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300'
              : 'bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300'
          } disabled:cursor-not-allowed`}
        >
          {actionLoading || channel.status === 'starting' || channel.status === 'stopping' ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              {actionLoading === 'start' || channel.status === 'starting' ? 'Starting...' : 'Stopping...'}
            </span>
          ) : currentStatus.showStart ? (
            'Start'
          ) : (
            'Stop'
          )}
        </button>

        {/* View details button */}
        <button
          onClick={() => onSelect?.(channel)}
          className="px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm transition-colors"
        >
          Details
        </button>

        {/* Delete button */}
        {channel.status === 'stopped' && (
          <button
            onClick={handleDelete}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete channel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ChannelCard;
